import React, { useState } from 'react'
import {
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip
} from '@mui/material'
import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Security as SecurityIcon
} from '@mui/icons-material'
import { useOktaAuth } from '../../contexts/OktaAuthContext'
import config from '../../config'

const UserProfile: React.FC = () => {
  const { user, logout, isAuthenticated } = useOktaAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  // Don't render if Okta is disabled or user is not authenticated
  if (!config.okta.enabled || !isAuthenticated || !user) {
    return null
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleClose()
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const displayName = user.name || user.preferred_username || user.email || 'User'
  const email = user.email || user.preferred_username || ''

  return (
    <Box>
      <Tooltip title="User Profile">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.main',
              fontSize: '0.875rem'
            }}
          >
            {getInitials(displayName)}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 280,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {getInitials(displayName)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {displayName}
              </Typography>
              {email && (
                <Typography variant="caption" color="textSecondary">
                  {email}
                </Typography>
              )}
            </Box>
          </Box>

          <Box mt={1}>
            <Chip
              icon={<SecurityIcon />}
              label="Authenticated"
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider />

        {/* User Details */}
        <MenuItem disabled>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">
              <strong>Name:</strong> {displayName}
            </Typography>
          </ListItemText>
        </MenuItem>

        {email && (
          <MenuItem disabled>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">
                <strong>Email:</strong> {email}
              </Typography>
            </ListItemText>
          </MenuItem>
        )}

        <Divider />

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Sign Out</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default UserProfile
